import React from 'react';
import { useForm } from 'react-hook-form';
import { debugZodResolver } from '@/lib/debugZodResolver';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import GoogleIcon from '@/components/svg/GoogleIcon.tsx';

import useCreateUser from '@/features/auth/signup/hooks/useCreateUser.ts';
import useCreateUserWithGoogle from '@/features/auth/signup/hooks/useCreateUserWithGoogle.ts';
import {
  userCredentialsSchema,
  type SignupFormData,
} from '../schemas/userCredentialsSchema.ts';
import AuthFormContainer from '@/features/auth/signup/components/AuthFormContainer.tsx';
import EmailInput from '@/components/form/EmailInput.tsx';
import PasswordInput from '@/components/form/PasswordInput.tsx';
import { useNavigate } from '@tanstack/react-router';
import { signupRoute } from '@/features/auth/signup/routes/signupRoute.ts';
import { readInvitationFromRedirect } from '@/features/public/acceptInvitation/utils/invitationFromRedirect.ts';
import { Description } from '@/components/ui/Title.tsx';

const SignupPage = () => {
  const navigate = useNavigate();
  const { redirect } = signupRoute.useSearch();
  const invitation = readInvitationFromRedirect(redirect);

  const { mutate: createUser, isPending: isLoadingSignup } =
    useCreateUser(redirect);
  const { mutate: createUserWithGoogle, isPending: isLoadingGoogle } =
    useCreateUserWithGoogle(redirect);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: debugZodResolver(userCredentialsSchema),
    defaultValues: { email: invitation?.email ?? '' },
  });

  const onSubmit = (data: SignupFormData) => {
    createUser({
      email: data.email,
      password: data.password,
    });
  };

  return (
    <AuthFormContainer title="Załóź konto">
      {invitation && (
        <Description className="w-full text-center">
          {invitation.companyName
            ? `Dołączasz do firmy ${invitation.companyName}`
            : 'Dołączasz do zespołu'}
        </Description>
      )}
      <div className="flex w-full flex-col items-end gap-6">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col items-start gap-8"
        >
          <div className="w-full space-y-4">
            <EmailInput
              label="Adres e-mail"
              register={register('email')}
              required={true}
              error={errors.email?.message}
            />

            <PasswordInput
              label="Hasło"
              register={register('password')}
              required={true}
              error={errors.password?.message}
            />
          </div>

          <ActionButton
            type="submit"
            tone="accent"
            size="block"
            isLoading={isLoadingSignup}
          >
            Dalej
          </ActionButton>
        </form>

        <div className="flex w-full flex-col items-center gap-4">
          <div className="w-full text-center text-sm font-normal leading-[22.4px] text-[#2E2E38]">
            lub
          </div>
          <ActionButton
            tone="outline"
            size="block"
            onClick={() => createUserWithGoogle()}
            disabled={isLoadingGoogle}
          >
            <GoogleIcon /> Kontynuuj z Google
          </ActionButton>
        </div>

        <div className="flex w-full items-start justify-center gap-2">
          <span className="text-center text-sm font-normal leading-[22.4px] text-[#2E2E38]">
            Masz już konto?
          </span>
          <a
            href="/auth/login"
            className="text-sm font-normal leading-[22.4px] text-[#0B6FC1] underline"
            onClick={(e) => {
              e.preventDefault();
              navigate({
                to: '/auth/login',
                search: redirect ? { redirect } : {},
              });
            }}
          >
            Zaloguj się
          </a>
        </div>
      </div>
    </AuthFormContainer>
  );
};

export default SignupPage;
